import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "wu_install_dismissed_at";
const SNOOZE_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < SNOOZE_DAYS * 86400000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt — show the manual steps instead.
    const t = isIos() ? setTimeout(() => setShow(true), 2500) : null;

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (t) clearTimeout(t);
    };
  }, []);

  const close = (snooze) => {
    if (snooze) localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    }
    close(true);
  };

  if (!show) return null;

  const steps = isIos()
    ? [
        <>Tap the <Share size={13} className="inline -mt-0.5" style={{ color: "var(--lime)" }} /> Share button at the bottom of Safari</>,
        <>Scroll down and tap &ldquo;Add to Home Screen&rdquo;</>,
        <>Tap &ldquo;Add&rdquo; in the top right corner</>,
      ]
    : [
        <>Open your browser menu</>,
        <>Tap &ldquo;Install app&rdquo; or &ldquo;Add to Home screen&rdquo;</>,
        <>Confirm and it lands on your home screen</>,
      ];

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      onClick={() => close(true)}
      data-testid="install-prompt"
    >
      <div
        className="w-full max-w-md rounded-3xl p-6 relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-lime)", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => close(true)}
          className="absolute top-4 right-4 p-1.5 rounded-xl transition hover:bg-white/10"
          style={{ color: "var(--text-muted)" }}
          aria-label="Close"
          data-testid="install-prompt-close"
        >
          <X size={16} />
        </button>

        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 mx-auto"
          style={{ background: "var(--lime-glow)", border: "1px solid var(--border-lime)" }}
        >
          <Download size={20} style={{ color: "var(--lime)" }} />
        </div>

        <h2 className="h-display text-2xl mb-1.5 text-center">Install the App</h2>
        <p className="text-sm mb-5 text-center" style={{ color: "var(--text-muted)" }}>
          Get the full app experience with faster access and offline support.
        </p>

        <div className="font-mono text-xs uppercase tracking-wider mb-3" style={{ color: "var(--lime)" }}>
          How to install
        </div>
        <ol className="space-y-3 mb-6">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm" style={{ color: "var(--text)" }}>
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-mono text-[11px]"
                style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }}
              >
                {i + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>

        <button onClick={install} className="btn-primary w-full mb-2" data-testid="install-prompt-confirm">
          {deferred ? "Install now" : "Got it!"}
        </button>
        <button
          onClick={() => close(true)}
          className="w-full py-2.5 text-sm transition hover:opacity-80"
          style={{ color: "var(--text-muted)" }}
          data-testid="install-prompt-later"
        >
          Maybe Later
        </button>
      </div>
    </div>,
    document.body
  );
}
